import { z } from "zod";

import { SHORTFALL_REASONS } from "@pharmacy/domain";
import { productResponse } from "../catalog/products";
import { moneyNotNegative, moneyText } from "../money";

/**
 * Moving stock between branches, in two steps, as the owner agreed.
 *
 * SEND: the sender picks the branch it goes to, then each medicine — which
 * batch, and how many. It leaves the sending branch at once.
 *
 * RECEIVE: the other branch says which of its own medicines each one is (or
 * adds it, with its own prices), counts what arrived, and gives a reason for
 * any shortfall. What arrives keeps its lot number, expiry and cost.
 *
 * Quantities are in each medicine's smallest unit.
 */

export const shortfallReason = z.enum(SHORTFALL_REASONS);

/** One medicine on its way: which batch, how many, and the size it was counted in. */
export const transferLineRequest = z.object({
  /** The batch it is taken from, which the sender picks. */
  batchId: z.string().uuid(),
  /** Always in the smallest unit; the phone works it out from the size chosen. */
  qtyBase: z.number().int().positive("How many?"),
  /** The size it was counted in — a strip, a box — to show at the other end. Absent for the smallest unit. */
  unitId: z.string().uuid().optional(),
});

export const createTransferRequest = z.object({
  /** Made on the phone, so a repeated tap sends it once. */
  id: z.string().uuid(),
  toBranchId: z.string().uuid(),
  note: z.string().trim().max(300).nullable().optional(),
  lines: z.array(transferLineRequest).min(1, "Add something to send").max(100),
});

/**
 * Sent to several branches at once, as the owner asked: one transfer each,
 * all sent together or none.
 */
export const createTransfersRequest = z.object({
  transfers: z.array(createTransferRequest).min(1).max(20),
});

/** A transfer changed before it was received: its whole new list. */
export const editTransferRequest = z.object({
  lines: z.array(transferLineRequest).min(1, "Add something to send").max(100),
});

/** Within a week of receipt, the sender corrects what was sent. */
export const correctSentRequest = z.object({
  lines: z.array(z.object({ lineId: z.string().uuid(), qtySentBase: z.number().int().positive() })).min(1),
});

/** Within a week of receipt, the receiver corrects what arrived. */
export const correctArrivedRequest = z.object({
  lines: z
    .array(
      z.object({
        lineId: z.string().uuid(),
        qtyReceivedBase: z.number().int().min(0),
        shortfallReason: shortfallReason.nullable().optional(),
        note: z.string().trim().max(300).nullable().optional(),
      }),
    )
    .min(1),
});

export const transferLineResponse = z.object({
  id: z.string().uuid(),
  /** The sending branch's batch it was taken from. */
  batchId: z.string().uuid(),
  /** The sending branch's medicine. */
  productId: z.string().uuid(),
  productName: z.string(),
  strength: z.string().nullable(),
  baseUnit: z.string(),
  batchNo: z.string().nullable(),
  expiryDate: z.string().nullable(),
  /** Null for somebody not allowed to see cost prices. */
  costPerBase: moneyText.nullable(),
  qtySentBase: z.number().int(),
  /** The size it was sent in, as the sender chose: "strip (10)". Null for the smallest unit. */
  sentSize: z.object({ name: z.string(), multiplier: z.number().int() }).nullable(),
  /** Null until received. In the SENDING medicine's smallest unit, to compare with what was sent. */
  qtyReceivedBase: z.number().int().nullable(),
  shortfallReason: shortfallReason.nullable(),
  note: z.string().nullable(),
  /** The receiving branch's medicine it became. Null until received. */
  receivedAsProductId: z.string().uuid().nullable(),
  receivedAsName: z.string().nullable(),
  /**
   * For the receiver: which of their medicines it most likely is — the one
   * it was received as last time, or one with the same name and strength
   * counted in the same unit. Null when there is none.
   */
  suggestedProductId: z.string().uuid().nullable(),
  /** The sending medicine's sizes, for adding it as new at the other end. */
  sizes: z.array(z.object({ name: z.string(), multiplier: z.number().int(), sellPrice: moneyText })),
});

export const transferStatus = z.enum(["dispatched", "received", "discrepancy", "cancelled"]);

export const transferResponse = z.object({
  id: z.string().uuid(),
  fromBranchId: z.string().uuid(),
  fromBranchName: z.string(),
  toBranchId: z.string().uuid(),
  toBranchName: z.string(),
  /** Sent and not yet received; received in full; received short. */
  status: transferStatus,
  sentAt: z.string(),
  sentByName: z.string(),
  receivedAt: z.string().nullable(),
  receivedByName: z.string().nullable(),
  note: z.string().nullable(),
  lines: z.array(transferLineResponse),
  /** Everything done to it since it was sent, oldest first. */
  changes: z.array(
    z.object({
      byName: z.string(),
      at: z.string(),
      kind: z.enum(["edited", "cancelled", "sent_corrected", "arrived_corrected"]),
      /** Each medicine changed, in its smallest unit. */
      details: z.array(z.object({ name: z.string(), baseUnit: z.string(), from: z.number().int(), to: z.number().int() })),
    }),
  ),
  /** For the branch the phone is in: cancel or edit it (sender, until it is received)… */
  canCancel: z.boolean(),
  canEdit: z.boolean(),
  /** …and, for a week after receipt, correct what was sent (sender) or what arrived (receiver). */
  canCorrectSent: z.boolean(),
  canCorrectArrived: z.boolean(),
});

export const transferSummary = z.object({
  id: z.string().uuid(),
  fromBranchName: z.string(),
  toBranchName: z.string(),
  status: transferStatus,
  sentAt: z.string(),
  medicineCount: z.number().int(),
});

export const transfersResponse = z.object({
  /** Sent to this branch and not received yet. */
  toReceive: z.array(transferSummary),
  /** Received here lately. */
  received: z.array(transferSummary),
  /** Sent from this branch lately. */
  sent: z.array(transferSummary),
});

/** Every other branch of the pharmacy: where stock can be sent. */
export const transferDestinationsResponse = z.object({
  branches: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});

export const receiveTransferRequest = z.object({
  lines: z
    .array(
      z.object({
        lineId: z.string().uuid(),
        qtyReceivedBase: z.number().int().min(0),
        shortfallReason: shortfallReason.nullable().optional(),
        note: z.string().trim().max(300).nullable().optional(),
        /**
         * Which of the receiving branch's medicines it is — one counted in the
         * same unit, or in one the two convert between (tablets into strips
         * of 10) — or a new one,
         * added with the sending medicine's name and sizes and the prices
         * this branch types.
         */
        as: z.union([
          z.object({ productId: z.string().uuid() }),
          z.object({
            newSizes: z
              .array(
                z.object({
                  name: z.string().trim().min(1).max(40),
                  multiplier: z.number().int().positive(),
                  sellPrice: moneyNotNegative(),
                }),
              )
              .min(1),
          }),
        ]),
      }),
    )
    .min(1),
});

/**
 * The working branch's own medicines, for the web's Send stock and Receive
 * (the phone reads them from its own copy). Behind the Transfers switch, so
 * somebody who may move stock can find it without the Products switch.
 * Either a search (`q`, the best 12) or exactly these (`ids`, comma-separated).
 */
export const transferMedicinesQuery = z.object({
  q: z.string().trim().max(120).optional(),
  ids: z
    .string()
    .max(2000)
    .optional()
    .transform((v) => (v ? v.split(",").map((id) => id.trim()).filter(Boolean) : undefined))
    .pipe(z.array(z.string().uuid()).max(50).optional()),
});

/** Each with its sizes and its batches on the shelf. */
export const transferMedicinesResponse = z.object({ products: z.array(productResponse) });

export type TransferMedicinesQuery = z.infer<typeof transferMedicinesQuery>;
export type TransferMedicinesResponse = z.infer<typeof transferMedicinesResponse>;
export type ShortfallReasonCode = z.infer<typeof shortfallReason>;
export type CreateTransferRequest = z.infer<typeof createTransferRequest>;
export type TransferLineResponse = z.infer<typeof transferLineResponse>;
export type TransferResponse = z.infer<typeof transferResponse>;
export type TransferSummary = z.infer<typeof transferSummary>;
export type TransfersResponse = z.infer<typeof transfersResponse>;
export type TransferDestinationsResponse = z.infer<typeof transferDestinationsResponse>;
export type ReceiveTransferRequest = z.infer<typeof receiveTransferRequest>;
export type TransferLineRequest = z.infer<typeof transferLineRequest>;
export type CreateTransfersRequest = z.infer<typeof createTransfersRequest>;
export type EditTransferRequest = z.infer<typeof editTransferRequest>;
export type CorrectSentRequest = z.infer<typeof correctSentRequest>;
export type CorrectArrivedRequest = z.infer<typeof correctArrivedRequest>;
